import "./CourseOfSalesComponent.scss";
import template from "./CourseOfSalesComponent.html";
import {IOnDestroy, IOnInit} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {ComponentCallback} from "../../types/AngularJS";
import {CourseOfSalesService, Sales} from "../../services/course-of-sales/CourseOfSalesService";
import {CompanyInfo} from "../../types/Company";
import {CodeAndExchange, securityEquality} from "../../instruments/types/CodeAndExchange";
import {ObjectUtil} from "../../js/util/ObjectUtil";
import {MarketUtil} from "../../js/util/MarketUtil";
import {PriceStrategyService} from "../inputs/price/service/PriceStrategyService";
import {PriceStrategy} from "../inputs/price/input/PriceStrategy";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {IdleEvent, SystemEvent} from "../../services/system-state/SystemEvent";

export const CourseOfSales = "tfCourseOfSales";

export function courseOfSales() {
    return {
        template,
        bindings: {
            security: "<",
            onSecurityChanged: "&"
        },
        controllerAs: "ctrl",
        controller: CourseOfSalesController
    };
}

const DataLoadIntervalSeconds = 15;
const DataLoadInterval = DataLoadIntervalSeconds * 1000;

export class CourseOfSalesController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
            this.strategy = this.priceStrategyService.forCode(security.code);
        }
    }

    public readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    public readonly currentSecurity$ = new ReplaySubject<string>(1);
    public readonly sales$ = new ReplaySubject<Sales>(1);

    public strategy: PriceStrategy;

    private readonly updated$ = new BehaviorSubject(true);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly courseOfSalesService: CourseOfSalesService,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly systemEventService: SystemEventService) {

    }

    public $onInit(): void {
        this.watchSecurity();
        this.watchSalesSources();
    }

    private watchSecurity() {
        this.security$
            .takeUntil(this.destroyed$)
            .map((s) => s.code)
            .distinctUntilChanged()
            .subscribe((code) => this.currentSecurity$.next(code));
    }

    private watchSalesSources() {
        const uniqueSecurity$ = this.security$
                                    .distinctUntilChanged(securityEquality());
        const delay$ = Observable.of(1).delay(DataLoadInterval);
        const pause$ = this.systemEventService
                           .idle()
                           .map((evt) => (evt === SystemEvent.USER_IDLE) || !MarketUtil.extendedMarketIsOpen())
                           .startWith(false);
        // Starts with a single emission to always call at least once, even outside market hours
        const refresh$ = this.updated$
                             .switchMap(() => delay$)
                             .startWith(0)
                             .withLatestFrom(pause$)
                             .map(([_, pause]) => pause)
                             .startWith(false);

        Observable.combineLatest(uniqueSecurity$, refresh$)
                  .takeUntil(this.destroyed$)
                  .switchMap(([security, pause]) => this.retrieve(security, pause))
                  .subscribe((sales) => this.sales$.next(sales));
    }

    private retrieve(security: CodeAndExchange, pause: boolean): Observable<Sales> {
        if (pause) {
            return Observable.empty<Sales>().finally(() => this.updated$.next(true));
        } else {
            return Observable.of(security)
                             .flatMap((s) => this.courseOfSalesService.courseOfSales(s))
                             .finally(() => this.updated$.next(true))
                             .catch((err) => Observable.empty<Sales>());
        }
    }

    public selectSecurity(company?: CompanyInfo) {
        if (company != null) {
            const security = ObjectUtil.pick(company, "code", "exchange");
            this.security$.next(security);
            this.onSecurityChanged({security});
        }
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
